import React from 'react';
import dayjs from 'dayjs';
import Modal from '../modal/Modal';
import { Col, Row } from '../../lib/utils';
import { useFacilityTransfers, useTransferFacilitySubscription } from '@/features/facilitySubscription/hooks';
import { FacilityTransferKind, FacilityTransferRecord } from '@/features/facilitySubscription/types';
import { clearPendingFacilityTransfer, loadPendingFacilityTransfer } from '@/features/facilitySubscription/transferPending';
import {
    classifyFacilityTransferFailure,
    buildTransferDoneMessage,
    describeTransferredForRefund,
    didLockerMove,
    getTransferDisplayStatusStyle,
    needsLockerReassign,
    FACILITY_TRANSFER_KIND_BADGE,
    isFacilityTransferUnsupported,
} from '@/features/facilitySubscription/transfer';

const fmt = (value?: string | null) => (value ? dayjs(value).format('YY.MM.DD') : '-');

/** 목록·카드에 붙이는 양도 배지. 표식이 없으면(구서버 포함) 아무것도 그리지 않는다. */
export const TransferKindBadge = ({ kind, size = 'md' }: { kind?: FacilityTransferKind; size?: 'sm' | 'md' }) => {
    if (!kind) return null;
    const cfg = FACILITY_TRANSFER_KIND_BADGE[kind];
    if (!cfg) return null;
    return (
        <span
            className={`inline-flex items-center rounded-[6px] border border-solid font-bold whitespace-nowrap ${
                size === 'sm' ? 'px-[5px] py-[1px] text-[10px]' : 'px-[8px] py-[2px] text-[13px]'
            }`}
            style={{ backgroundColor: cfg.bg, color: cfg.color, borderColor: cfg.border }}
        >
            {cfg.label}
        </span>
    );
};

type ListProps = {
    centerId: string;
    memberId?: string;
    subscriptionId?: string;
    /** 이 회원 입장에서 「넘김/받음」을 표시한다(회원 기준 이력) */
    viewerMemberId?: string;
    createAlert?: any;
    limit?: number;
};

/**
 * 양도 이력(가볍게). 「마무리 필요」는 원본은 줄었는데 받는 회원 이용권이 아직 안 만들어진 상태다 —
 * 그 기록의 요청 키·내용 그대로 다시 보내면 서버가 이어서 완료한다.
 */
export const SubscriptionTransferHistoryList = ({ centerId, memberId, subscriptionId, viewerMemberId, createAlert, limit = 20 }: ListProps) => {
    const { data, isLoading, isError, error, refetch } = useFacilityTransfers({ centerId, memberId, subscriptionId, limit });
    const completeMutation = useTransferFacilitySubscription();

    const handleComplete = async (record: FacilityTransferRecord) => {
        try {
            const result = await completeMutation.mutateAsync({
                centerId,
                subscriptionId: record.sourceId,
                fromMemberId: record.fromMemberId,
                body: {
                    clientOperationId: record.clientOperationId,
                    toMemberId: record.toMemberId,
                    days: record.days,
                    startDate: dayjs(record.targetStartDate).format('YYYY-MM-DD'),
                    moveLocker: record.moveLocker === true,
                },
            });
            // 이 창에 보관된 같은 키의 미확정 요청이 있으면 끝났으니 지운다(다른 키면 건드리지 않는다).
            const pending = loadPendingFacilityTransfer(record.sourceId);
            if (result?.transfer && pending?.body.clientOperationId === record.clientOperationId) {
                clearPendingFacilityTransfer(record.sourceId);
            }
            // 이어 완료에서도 칸이 그 사이 다른 회원에게 갔으면 칸 없이 만들어진다 — 응답 경고를 그대로 보여 준다.
            createAlert?.(
                '마무리 완료',
                result ? buildTransferDoneMessage(result, record.toMemberName || '') : `${record.toMemberName || '받는 회원'}님 이용권까지 만들어졌어요.`,
                false,
            );
            refetch();
        } catch (e) {
            createAlert?.('마무리 실패', classifyFacilityTransferFailure(e).message, true);
        }
    };

    if (isLoading) {
        return (
            <Col className="gap-[8px]">
                {[0, 1].map((i) => (
                    <div key={i} className="h-[56px] rounded-[10px] bg-[#F1F5F9] animate-pulse" />
                ))}
            </Col>
        );
    }
    if (isError) {
        return (
            <span className="text-[13px] text-[#94A3B8]">
                {isFacilityTransferUnsupported(error) ? '이 서버에서는 아직 양도 이력을 볼 수 없어요.' : '양도 이력을 불러오지 못했어요.'}
            </span>
        );
    }
    const rows = data?.results ?? [];
    if (rows.length === 0) return <span className="text-[13px] text-[#94A3B8]">양도 이력이 없어요.</span>;

    return (
        <Col className="gap-[8px]">
            {rows.map((record) => {
                // 모르는 상태값을 「완료」로 대체하지 않는다(되돌림·확인 필요는 그대로 보인다).
                const status = getTransferDisplayStatusStyle(record);
                const direction =
                    viewerMemberId && String(record.toMemberId) === String(viewerMemberId)
                        ? '받음'
                        : viewerMemberId && String(record.fromMemberId) === String(viewerMemberId)
                        ? '넘김'
                        : null;
                return (
                    <Col key={record._id} className="gap-[4px] px-[14px] py-[10px] rounded-[10px] border border-solid border-[#E5E7EB] bg-white">
                        <Row className="justify-between items-center gap-[8px]">
                            <Row className="items-center gap-[6px] min-w-0 flex-wrap">
                                {direction && (
                                    <span
                                        className="px-[6px] py-[1px] rounded-[4px] text-[11px] font-bold"
                                        style={{
                                            backgroundColor: direction === '받음' ? '#EEF2FF' : '#F1F5F9',
                                            color: direction === '받음' ? '#4338CA' : '#475569',
                                        }}
                                    >
                                        {direction}
                                    </span>
                                )}
                                <span className="text-[14px] font-semibold text-[#111827]">
                                    {record.fromMemberName || '알 수 없음'} → {record.toMemberName || '알 수 없음'}
                                </span>
                                <span className="text-[13px] text-[#64748B]">
                                    · {record.productName} {record.days}일{record.isFull ? '(전부)' : ''}
                                </span>
                            </Row>
                            <span
                                className="px-[6px] py-[1px] rounded-[4px] text-[11px] font-bold shrink-0"
                                style={{ backgroundColor: status.bg, color: status.color }}
                            >
                                {status.label}
                            </span>
                        </Row>
                        <Row className="justify-between items-center gap-[8px]">
                            <span className="text-[12px] text-[#94A3B8]">
                                {dayjs(record.createdAt).format('YY.MM.DD HH:mm')} · 받은 기간 {fmt(record.targetStartDate)} ~ {fmt(record.targetEndDate)}
                                {didLockerMove(record) ? ' · 락커 칸 함께' : ''}
                                {record.actor?.name ? ` · ${record.actor.name}` : ''}
                            </span>
                            {/* 처리 중(10분 안)도 허용: 같은 키 재전송은 서버가 재생·이어서 완료로 처리한다 */}
                            {(record.displayStatus === 'NEEDS_COMPLETION' || record.displayStatus === 'PROCESSING') && createAlert && (
                                <button
                                    type="button"
                                    disabled={completeMutation.isLoading}
                                    onClick={() => handleComplete(record)}
                                    className="h-[28px] px-[10px] rounded-[6px] border-0 bg-[#D97706] text-white text-[12px] font-bold cursor-pointer shrink-0"
                                >
                                    {completeMutation.isLoading ? '처리 중…' : '이어서 완료'}
                                </button>
                            )}
                        </Row>
                        {needsLockerReassign(record) && (
                            <span className="text-[12px] font-semibold text-[#B45309]">
                                락커 칸은 넘어가지 않았어요 — 받는 회원에게 다른 칸을 배정해 주세요.
                            </span>
                        )}
                    </Col>
                );
            })}
        </Col>
    );
};

/** 회원 기준 양도 이력 창 */
export const SubscriptionTransferHistoryModal = ({
    centerId,
    memberId,
    memberName,
    onClose,
    createAlert,
}: {
    centerId: string;
    memberId: string;
    memberName?: string;
    onClose: () => void;
    createAlert?: any;
}) => (
    <Modal whiteViewWidth={560} isCloseButton isCloseOutside onClose={onClose} isWhiteOverFlowScroll={false}>
        <Col className="px-[28px] pt-[26px] pb-[24px] gap-[16px]">
            <Col className="gap-[2px] pr-[40px]">
                <span className="text-[20px] font-bold text-[#111827]">이용권 양도 이력</span>
                {memberName && <span className="text-[14px] text-[#64748B]">{memberName}님이 넘기거나 받은 이용권</span>}
            </Col>
            <div className="max-h-[60vh] overflow-y-auto">
                <SubscriptionTransferHistoryList centerId={centerId} memberId={memberId} viewerMemberId={memberId} createAlert={createAlert} limit={50} />
            </div>
            <Row className="justify-end">
                <button
                    type="button"
                    onClick={onClose}
                    className="h-[40px] px-[20px] rounded-[10px] border border-solid border-[#D1D5DB] bg-white text-[14px] font-bold text-[#374151] cursor-pointer"
                >
                    닫기
                </button>
            </Row>
        </Col>
    </Modal>
);

/**
 * 일부 양도한 원본 환불용 정보. 환불 창을 열 때마다 이력을 **새로** 읽고, 다 읽기 전에는 ready=false 다
 * (호출부는 그동안 환불 버튼을 끈다). 넘긴 기간까지 돌려주는 실수를 막으려고 누구에게 며칠을 넘겼는지
 * 먼저 보여 준다. 환불 금액은 관리자가 직접 정한다(사용 일수 기준 환불 계산은 시스템에 없다).
 */
export const useTransferredRefundInfo = ({
    centerId,
    subscriptionId,
    enabled,
}: {
    centerId: string;
    subscriptionId: string;
    enabled: boolean;
}) => {
    const query = useFacilityTransfers({ centerId, subscriptionId, limit: 100, enabled });
    const loaded = enabled && query.isSuccess && !query.isFetching;
    const described = loaded ? describeTransferredForRefund(subscriptionId, query.data?.results) : { items: [], pendingCount: 0 };
    // 첫 페이지(최대 100건)만 읽는다. 전체가 더 많으면 넘긴 기간을 다 확인하지 못했으니 환불을 막는다.
    const readCount = query.data?.results?.length ?? 0;
    const totalCount = Number(query.data?.total ?? query.data?.totalResults ?? readCount);
    const truncated = loaded && totalCount > readCount;
    return {
        // 처리 중·상태 불명 양도가 있거나 이력을 다 못 읽었으면 넘긴 기간이 확정되지 않았다 — 환불을 막는다.
        ready: loaded && described.pendingCount === 0 && !truncated,
        loaded,
        truncated,
        pendingCount: described.pendingCount,
        isError: enabled && query.isError,
        items: described.items,
        refetch: query.refetch,
    };
};

export const TransferredRefundNotice = ({ info }: { info: ReturnType<typeof useTransferredRefundInfo> }) => (
    <div className="rounded-[10px] border border-solid border-[#FED7AA] bg-[#FFF7ED] px-[14px] py-[10px] text-[13px] leading-[1.6] text-[#9A3412]">
        <b>이 이용권은 기간 일부를 양도했어요.</b>
        {!info.loaded && !info.isError && <span className="block">양도 기록을 확인하는 중이에요…</span>}
        {info.truncated && (
            <span className="block font-semibold">
                양도 기록이 너무 많아 여기서 모두 확인할 수 없어 환불을 막았어요. 고객센터에 문의해 주세요.
            </span>
        )}
        {info.loaded && info.pendingCount > 0 && (
            <span className="block font-semibold">
                아직 처리 중인 양도가 {info.pendingCount}건 있어 지금은 환불할 수 없어요. 양도 이력에서 마무리한 뒤 환불해 주세요.
            </span>
        )}
        {info.isError && (
            <span className="block">
                양도 기록을 불러오지 못해 환불할 수 없어요.{' '}
                <button type="button" onClick={() => info.refetch()} className="underline bg-transparent border-0 p-0 text-[#9A3412] cursor-pointer">
                    다시 불러오기
                </button>
            </span>
        )}
        {info.items.map((item) => (
            <span key={item.transferId} className="block">
                · {item.days}일을 {item.toMemberName ? `${item.toMemberName}님께` : '다른 회원에게'} 넘겼습니다 ({fmt(item.at)})
            </span>
        ))}
        {info.ready && <span className="block mt-[4px]">넘긴 기간은 빼고, 돌려줄 금액을 직접 적어 주세요.</span>}
    </div>
);
